import { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAuthUserSlice } from '../../../../store/auth-user-slice'
import { isSessionStillResolving, selectAuthStateSlice, setAuthState } from '../../../../store/auth-state-slice'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'

import { FrontendUrls } from '../../../../types/urls'
import { CrispSegmentsEnum } from '../../../../types/crisp'
import { hideAvailabilityTooltip, setCrispSessionInfo } from '../../../../util/set-crisp-session-info'
import { saveSubscriptionPeriodFromQuery } from '../../subscription-period'
import { setSignupEmail, trackSignup } from '../analytics'
import { ISignupQueryParams } from '../query-params'

// Only what the NEXT step needs. The GDPR and SOC2 icons are rendered by this page's own left
// column, so preloading them duplicates requests the browser is already making.
const PRELOADED_ASSETS = ['/signup/testimonial-avatar.png']

export interface IUseSignupBootstrapParams {
  queryParams: ISignupQueryParams
  search: string
  /** The prefilled address, so the events fired before any submit already carry it. */
  initialEmail: string
}

/**
 * Everything the page does because it was opened, rather than because someone acted: asset
 * preloading, one-time storage and analytics work, and the navigation away once a session
 * already exists.
 *
 * Returns true once a live session means this page is on its way out, so the caller can skip
 * rendering a form the visitor would only see flash past.
 */
export const useSignupBootstrap = ({ queryParams, search, initialEmail }: IUseSignupBootstrapParams): boolean => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authState } = useAppSelector(selectAuthStateSlice)

  // A visitor who is already signed in has no sign-up to do. `?redirect` cannot reach this
  // point, because the effect below hands that whole query string to the sign-in page.
  const hasLiveSession = authState === AuthStateEnum.signedIn
  // Both reasons this page will not be the one the visitor ends up on. The hand-off used to be
  // missing here, so those two query params painted the whole form for a frame before pushing.
  const isLeaving = hasLiveSession || isSessionStillResolving(authState) || queryParams.handOffToSignin
  const hasBootstrapped = useRef(false)

  useEffect(() => {
    // None of this is for a visitor who is only passing through. Claiming the sign-up auth
    // state matters most: doing it on mount overwrote the unresolved `signedOut` that says a
    // session may still be coming, and losing that signal is what let the form flash.
    if (hasBootstrapped.current || hasLiveSession || isSessionStillResolving(authState)) {
      return
    }
    hasBootstrapped.current = true

    // Sign-in owns both of those params, so hand the whole query string over untouched.
    if (queryParams.handOffToSignin) {
      history.push(FrontendUrls.signin + search)
      return
    }

    PRELOADED_ASSETS.forEach((src) => {
      const image = new Image()
      image.src = src
    })

    dispatch(setAuthState(AuthStateEnum.createCompany))
    setSignupEmail(initialEmail)
    trackSignup('SIGNUP_PAGE_VIEWED')

    if (queryParams.subscriptionPlan) {
      localStorage.setItem('vtSubscriptionPlan', queryParams.subscriptionPlan)
    }
    saveSubscriptionPeriodFromQuery(queryParams.subscriptionPeriod)
    if (queryParams.affiliateCode) {
      localStorage.setItem('vtAffiliateCode', queryParams.affiliateCode)
    }

    setCrispSessionInfo([CrispSegmentsEnum.chat, CrispSegmentsEnum.support, CrispSegmentsEnum.dashboard, CrispSegmentsEnum.onSignupPage], { signupStep: 'signup-screen' })
    hideAvailabilityTooltip()
  }, [isLeaving])

  // The user arrives a moment after the session does, and the destination is derived from it,
  // so the navigation waits for it even though the render guard below does not.
  useEffect(() => {
    if (hasLiveSession && authUser.id) {
      history.push(FrontendUrls.dashboard)
    }
  }, [authState, authUser.id])

  // Derived while rendering, so the caller knows before the first paint. Waiting for the user
  // here too would draw the form across the gap between the session landing and the user
  // loading, which is exactly the blink this exists to stop.
  return isLeaving
}
